"use client";

import { useState } from "react";
import { Segmented } from "@/components/Segmented";
import { ServerTool } from "@/components/ServerTool";
import { formatBytes, outputName } from "@/lib/files";
import { getTool } from "@/lib/tools";

const tool = getTool("compress-pdf")!;

type Level = "screen" | "ebook" | "printer";

const LEVELS: { value: Level; label: string; hint: string }[] = [
  {
    value: "screen",
    label: "Strong",
    hint: "Smallest file. Pictures at screen quality (72 dpi) — fine for email.",
  },
  {
    value: "ebook",
    label: "Balanced",
    hint: "Good quality for reading on any screen (150 dpi). Best for most PDFs.",
  },
  {
    value: "printer",
    label: "Light",
    hint: "Keeps print quality (300 dpi); saves less space.",
  },
];

/** Compress PDF (Phase 5, server): Ghostscript rewrites the PDF smaller. */
export function CompressPdfTool() {
  const [level, setLevel] = useState<Level>("ebook");
  return (
    <ServerTool
      tool={tool}
      job="compress"
      options={{ level }}
      button="Compress PDF"
      result={(file, blob, note) => ({
        blob,
        name: outputName(file.name, "compressed", "pdf"),
        detail:
          note === "already-small"
            ? "Already as small as it gets — this is your original PDF"
            : `${formatBytes(file.size)} → ${formatBytes(blob.size)} (${Math.round((1 - blob.size / file.size) * 100)}% smaller)`,
      })}
    >
      <Segmented
        label="Compression"
        options={LEVELS}
        value={level}
        onChange={setLevel}
        stretch
      />
      <p className="text-sm text-ink">
        {LEVELS.find((l) => l.value === level)!.hint}
      </p>
    </ServerTool>
  );
}
